// src/lib/env.ts — read and validate the two Supabase env vars.
//
// The app is built to boot with placeholder values so you can run it before the
// Supabase project exists. Rather than crashing on a bad URL, we detect the
// unconfigured state and let the UI show setup instructions instead.

const RAW_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const RAW_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * Values that mean "you copied .env.example and haven't filled it in yet".
 * Checked as substrings so any of the placeholder shapes are caught.
 */
const PLACEHOLDER_MARKERS = [
  'your-project-ref',
  'your-anon-key',
  'your_project',
  'YOUR_',
  'changeme',
];

function isPlaceholder(value: string | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  if (trimmed === '') return true;
  return PLACEHOLDER_MARKERS.some((marker) =>
    trimmed.toLowerCase().includes(marker.toLowerCase()),
  );
}

/**
 * The API sub-path on a URL, if it has one, else null.
 *
 * Only these known prefixes count, so a self-hosted Supabase living under a
 * path of its own is left alone.
 */
function apiSubPath(value: string): string | null {
  try {
    const { pathname } = new URL(value);
    const match = pathname.match(/^\/(rest|auth|storage|realtime|functions|graphql)(\/.*)?$/);
    return match ? pathname.replace(/\/+$/, '') : null;
  } catch {
    return null;
  }
}

function looksLikeUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Human-readable reasons the app can't talk to Supabase yet. Empty = good. */
export const configProblems: string[] = (() => {
  const problems: string[] = [];

  if (isPlaceholder(RAW_URL)) {
    problems.push('VITE_SUPABASE_URL is missing or still the placeholder value.');
  } else if (!looksLikeUrl(RAW_URL!)) {
    problems.push(
      `VITE_SUPABASE_URL is not a valid URL ("${RAW_URL}"). It should look like https://abcdefgh.supabase.co`,
    );
  } else if (apiSubPath(RAW_URL!)) {
    // The dashboard's Data API page shows the REST endpoint
    // (https://<ref>.supabase.co/rest/v1/), which is easy to copy instead of
    // the Project URL. supabase-js appends /rest/v1 itself, so the mistake
    // passes every other check here and then 404s on every single request —
    // which looks like a broken app, not a typo in a config file.
    problems.push(
      `VITE_SUPABASE_URL has "${apiSubPath(RAW_URL!)}" on the end. Use just the project ` +
        'origin — https://<your-ref>.supabase.co — with no path. The client adds the rest.',
    );
  }

  if (isPlaceholder(RAW_KEY)) {
    problems.push('VITE_SUPABASE_ANON_KEY is missing or still the placeholder value.');
  }

  // Pasting a secret key here would be a serious mistake: it bypasses RLS, and
  // Vite would ship it to every browser that loads the app.
  //
  // Supabase has two generations of key naming and this has to catch both.
  //   legacy: anon / service_role, both long JWTs starting with eyJ
  //   current: sb_publishable_... / sb_secret_...
  // Only the second half of each pair is dangerous. The legacy service_role
  // key can be spotted because the role is spelled out in the payload; the
  // current one announces itself in the prefix.
  if (RAW_KEY && /(^|\.)sb_secret_/i.test(RAW_KEY.trim())) {
    problems.push(
      'VITE_SUPABASE_ANON_KEY is a secret key (sb_secret_…). That key bypasses ' +
        'Row Level Security and must never ship in the browser bundle. Use the ' +
        'publishable key (sb_publishable_…) from Settings → API Keys instead.',
    );
  } else if (RAW_KEY && RAW_KEY.includes('service_role')) {
    problems.push(
      'VITE_SUPABASE_ANON_KEY looks like a service_role key. That key bypasses ' +
        'Row Level Security and must never ship in the browser bundle. Use the ' +
        '"anon" / "public" key instead.',
    );
  }

  // Catch the two fields being swapped, which is easy to do when both are
  // copied from the same page and one of them is truncated on screen.
  if (RAW_KEY && /^https?:\/\//i.test(RAW_KEY.trim())) {
    problems.push('VITE_SUPABASE_ANON_KEY looks like a URL. The two values are the wrong way round.');
  }

  return problems;
})();

export const isSupabaseConfigured = configProblems.length === 0;

/**
 * URL handed to createClient. When unconfigured we substitute a syntactically
 * valid but non-existent host, because createClient throws on an invalid URL
 * and a thrown error at module load means a white screen with no explanation.
 * Nothing is ever requested from this host — the UI gates on
 * isSupabaseConfigured before any auth or data call.
 */
export const supabaseUrl = isSupabaseConfigured
  ? RAW_URL!.trim().replace(/\/+$/, '')
  : 'https://unconfigured.invalid';

export const supabaseAnonKey = isSupabaseConfigured ? RAW_KEY!.trim() : 'unconfigured';
